import Slider from '@react-native-community/slider';
import { useEffect, useState } from 'react';
import { Pressable, View, StyleSheet } from 'react-native';
import { IconButton, Surface, Text, useTheme } from 'react-native-paper';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
  cancelAnimation,
} from 'react-native-reanimated';

import { formatDuration } from '@/formatters/formatDuration';
import { iconSize } from '@/theme';

export type FloatingMediaBarVariant = 'recording' | 'playback';

export interface FloatingMediaBarProps {
  readonly variant: FloatingMediaBarVariant;
  readonly elapsedSeconds?: number;
  readonly durationMs?: number | null;
  readonly playbackPositionMs?: number;
  readonly isPaused: boolean;
  readonly title?: string;
  readonly labels: {
    status: string;
    playPause: string;
    stop: string;
  };
  readonly onAction: () => void;
  readonly onStop: () => void;
  readonly onSeek?: (positionMs: number) => void;
  readonly onExpand?: () => void;
}

export const FloatingMediaBar = ({
  variant,
  elapsedSeconds = 0,
  durationMs = 0,
  playbackPositionMs = 0,
  isPaused,
  title,
  labels,
  onAction,
  onStop,
  onSeek,
  onExpand,
}: FloatingMediaBarProps) => {
  const theme = useTheme();
  const opacity = useSharedValue(1);
  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrubPosition, setScrubPosition] = useState<number | null>(null);

  useEffect(() => {
    if (variant !== 'recording' || isPaused) {
      cancelAnimation(opacity);
      opacity.value = withTiming(1, { duration: 200 });
      return;
    }

    opacity.value = withRepeat(
      withSequence(withTiming(0.4, { duration: 800 }), withTiming(1, { duration: 800 })),
      -1,
      true,
    );

    return () => cancelAnimation(opacity);
  }, [opacity, isPaused, variant]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }));

  const dotColor = isPaused ? theme.colors.outline : theme.colors.error;
  const actionIcon = isPaused ? 'play' : 'pause';

  const recordingTime = formatDuration(elapsedSeconds * 1000);

  const safeDurationMs = durationMs ?? 0;
  const displayPositionMs = scrubPosition ?? playbackPositionMs;
  const positionTime = formatDuration(displayPositionMs);
  const totalTime = formatDuration(safeDurationMs);

  const handleSlidingStart = () => {
    setIsScrubbing(true);
  };

  const handleValueChange = (value: number) => {
    setScrubPosition(Math.round(value));
  };

  const handleSlidingComplete = (value: number) => {
    setIsScrubbing(false);
    setScrubPosition(null);
    onSeek?.(Math.round(value));
  };

  return (
    <Surface elevation={3} style={[styles.container, { backgroundColor: theme.colors.surface }]}>
      {variant === 'recording' ? (
        <Pressable style={styles.recordingContent} onPress={onExpand}>
          <Animated.View
            style={[styles.recordingDot, { backgroundColor: dotColor }, animatedStyle]}
          />
          <View style={styles.textContainer}>
            <Text variant="labelSmall" style={{ color: theme.colors.outline }}>
              {labels.status}
            </Text>
            <Text variant="titleMedium">{recordingTime}</Text>
          </View>
        </Pressable>
      ) : (
        <View style={styles.textContainer}>
          {title && (
            <Text variant="labelSmall" numberOfLines={1} style={{ color: theme.colors.outline }}>
              {title}
            </Text>
          )}
          <View style={styles.playbackRow}>
            <Text variant="titleMedium">{positionTime}</Text>
            <Text
              variant="bodySmall"
              style={{ color: theme.colors.outline, marginLeft: 4, alignSelf: 'center' }}>
              / {totalTime}
            </Text>
          </View>
          <Slider
            style={styles.slider}
            testID="slider"
            minimumValue={0}
            maximumValue={safeDurationMs > 0 ? safeDurationMs : 1}
            value={isScrubbing ? (scrubPosition ?? playbackPositionMs) : playbackPositionMs}
            minimumTrackTintColor={theme.colors.primary}
            maximumTrackTintColor={theme.colors.surfaceVariant}
            thumbTintColor={theme.colors.primary}
            onSlidingStart={handleSlidingStart}
            onValueChange={handleValueChange}
            onSlidingComplete={handleSlidingComplete}
          />
        </View>
      )}

      <IconButton
        icon={actionIcon}
        iconColor={theme.colors.primary}
        size={iconSize.lg}
        onPress={onAction}
        accessibilityLabel={labels.playPause}
      />
      <IconButton
        icon="stop-circle"
        iconColor={theme.colors.error}
        size={iconSize.lg}
        onPress={onStop}
        accessibilityLabel={labels.stop}
      />
    </Surface>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    right: 16,
    borderRadius: 28,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    paddingLeft: 24,
    minHeight: 72,
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
    marginRight: 8,
  },
  recordingContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  recordingDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  playbackRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  slider: {
    height: 28,
    marginTop: 2,
    marginLeft: -8,
    marginRight: -8,
  },
});
